/**
 * gridConfigs — ĐỌC `listinfo.field.grid_configs` VÀ ÁP VÀO CẤU HÌNH FIELD CỦA DataListManager.
 *
 * `grid_configs` KHÔNG phải JSON: nó là một BIỂU THỨC JS, khoá thường không đặt nháy —
 *   "{\"horizontal\":true,\"readOnly\":true,\"format\":\"0.00\",md:2,lg:2}"
 * `JSON.parse` hỏng ngay ở `md:`. Bộ cũ chạy nó bằng
 *   new Function("data","context","mediaQueryMatches", "return " + grid_configs)
 * (xem `GroupTabs.js:219-226`), nên nó còn có thể PHỤ THUỘC DỮ LIỆU:
 *   "{readOnly:true, md: data.__kieu_ky=='__today' ? 8 : 4}"
 *
 * Hai trường hợp, xử lý khác nhau ở ĐÚNG MỘT chỗ:
 *   - TĨNH  : chạy một lần là ra giá trị đúng.
 *   - ĐỘNG  : chạy với `data = {}` để lấy CỠ Ô mặc định. Đây là ảnh chụp, không phải giá trị đúng cho
 *             mọi bản ghi (`md: data.x ? 8 : 4` luôn ra nhánh 4) — nhưng có cỡ ô gần đúng vẫn hơn hẳn
 *             KHÔNG có cỡ ô nào: không có thì mọi field rơi về mặc định và cả phần master trông như
 *             lưới chia đều, không giống bố cục cũ chút nào.
 *             RIÊNG `readOnly` thì KHÔNG dùng ảnh chụp mà dịch sang SCRIPT — nó là quyền GHI, sai một
 *             cái là field đáng lẽ khoá lại cho sửa; script thì đúng theo từng bản ghi.
 *
 * File KHÔNG import gì để test node chạy được trên chính mã nguồn này.
 */

/** Cỡ lưới -> `gridEdit`. Bộ mới không có khoá nào khác tương đương nên luôn áp khi tĩnh. */
const GRID_SIZE_KEYS = ['xs', 'sm', 'md', 'lg'];

/**
 * Khoá của bộ cũ mà bộ mới KHÔNG có chỗ chứa — giữ lại làm tài liệu cho người đọc.
 * (Danh sách này KHÔNG còn là căn cứ cảnh báo: xem `unsupportedGridKeys`.)
 */
const IGNORED_KEYS = [
	'style', 'variant', 'color', 'autoFocus', 'InputLabelProps', 'id_container',
	'isRadio', 'inputType', 'notAdd', 'extraActionOfForm', 'inline_hide', 'horizontal',
];

/** Khoá bộ mới THẬT SỰ dùng tới. Mọi khoá ngoài danh sách này đều được cảnh báo ra console. */
const APPLIED_KEYS = [...GRID_SIZE_KEYS, 'readOnly', 'disabled', 'format', 'min', 'max', 'step', 'minWidth'];

/** Tên biến làm cho biểu thức phụ thuộc dữ liệu. */
const DYNAMIC_VARS = ['data', 'context', 'mediaQueryMatches'];

/** Bỏ chữ `return` mở đầu nếu có (bộ cũ chấp nhận cả hai dạng). */
function stripReturn(src) {
	return String(src || '').replace(/^\s*return\s+/, '').trim();
}

/**
 * Biểu thức có phụ thuộc dữ liệu không. Dò theo TỪ TRỌN VẸN để `metadata` hay `context_id` không bị
 * nhận nhầm là `data`/`context`.
 */
function isDynamicGridConfigs(raw) {
	const src = stripReturn(raw);
	if (!src) return false;
	return DYNAMIC_VARS.some(v => new RegExp('\\b' + v + '\\b').test(src));
}

/**
 * Đọc chuỗi `grid_configs`.
 * @returns {{ ok, value, dynamic, error }} — `value` chỉ đáng tin khi `ok && !dynamic`.
 */
function parseGridConfigs(raw) {
	const src = stripReturn(raw);
	if (!src) return { ok: false, value: null, dynamic: false, error: null };
	const dynamic = isDynamicGridConfigs(src);
	try {
		// Chạy với dữ liệu RỖNG: đủ để lấy giá trị của biểu thức tĩnh, và không ném với biểu thức động
		// (kết quả khi đó chỉ là một ảnh chụp, người gọi phải xét cờ `dynamic`).
		const fn = new Function('data', 'context', 'mediaQueryMatches', 'return ' + src);
		const value = fn({}, {}, true);
		if (!value || typeof value !== 'object' || Array.isArray(value)) {
			return { ok: false, value: null, dynamic, error: 'grid_configs không trả về object' };
		}
		return { ok: true, value, dynamic, error: null };
	} catch (e) {
		return { ok: false, value: null, dynamic, error: (e && e.message) || String(e) };
	}
}

/** Khoá đã khai mà bộ mới không dùng tới — để cảnh báo. */
/**
 * Khoá KHÔNG được áp dụng.
 *
 * Lọc theo danh sách khoá ĐƯỢC DÙNG chứ không theo danh sách khoá đã biết là bỏ: khoá lạ (`maxWidth`,
 * một khoá của phiên bản sau…) trước đây rơi vào khoảng trống giữa hai danh sách và bị bỏ **im lặng** —
 * đúng cái bẫy mà cả file này sinh ra để tránh. Cách lọc ngược này tự cập nhật: thêm khoá mới vào
 * `APPLIED_KEYS` là hết cảnh báo, quên thì cảnh báo còn đó.
 */
function unsupportedGridKeys(value) {
	if (!value || typeof value !== 'object') return [];
	return Object.keys(value).filter(k => APPLIED_KEYS.indexOf(k) < 0);
}

/** Số hợp lệ cho cỡ lưới MUI (1-12). Giá trị ngoài khoảng là khai sai, bỏ qua còn hơn vẽ vỡ bố cục. */
function gridSize(v) {
	const n = Number(v);
	if (!Number.isFinite(n)) return null;
	const i = Math.round(n);
	return (i >= 1 && i <= 12) ? i : null;
}

/**
 * Dịch `grid_configs` ĐỘNG thành script `readOnly` mà `parseDataListConfig` hiểu.
 * Tham số của `readOnly` là (context, formData, masterData, fields) — `formData` chính là `data` của bộ cũ.
 * Trả null nếu biểu thức không hề nhắc tới readOnly (khỏi sinh script vô nghĩa).
 */
function readOnlyScriptFrom(raw) {
	const src = stripReturn(raw);
	if (!src || !/\breadOnly\b/.test(src)) return null;
	return 'return !!((function(data, context, mediaQueryMatches){ return (' + src + '); })(formData, context, true) || {}).readOnly;';
}

/**
 * Áp `grid_configs` lên field ĐÃ DỊCH.
 *
 * Nguyên tắc: khoá khai TƯỜNG MINH ở cấp field của listinfo (`format`, `min`, `max`) THẮNG —
 * `grid_configs` chỉ lấp chỗ trống. Riêng cỡ lưới thì listinfo không có khoá cấp field nào tương
 * đương nên luôn áp.
 *
 * @param out  field đã dịch (bị sửa TẠI CHỖ và cũng được trả về)
 * @param raw  chuỗi grid_configs gốc
 * @returns {{ applied: string[], skipped: string[], ignored: string[], error: string|null, dynamic: boolean }}
 */
function applyGridConfigs(out, raw) {
	const rs = { applied: [], skipped: [], ignored: [], error: null, dynamic: false };
	if (!out || !raw) return rs;

	const parsed = parseGridConfigs(raw);
	rs.dynamic = parsed.dynamic;
	if (!parsed.ok) { rs.error = parsed.error; return rs; }

	// `readOnly` của biểu thức ĐỘNG: dịch sang SCRIPT (đúng theo từng bản ghi), không dùng ảnh chụp.
	if (parsed.dynamic) {
		const script = readOnlyScriptFrom(raw);
		if (script && out.readOnly === undefined) { out.readOnly = script; rs.applied.push('readOnly'); }
	}

	const v = parsed.value;
	const grid = {};
	for (const k of GRID_SIZE_KEYS) {
		if (v[k] === undefined) continue;
		const n = gridSize(v[k]);
		if (n === null) { rs.skipped.push(k); continue; }
		grid[k] = n;
		rs.applied.push(k);
	}
	if (Object.keys(grid).length){
		out.gridEdit = { ...(out.gridEdit || {}), ...grid };
		if(!out.gridEdit.lg && out.gridEdit.md) out.gridEdit.lg = out.gridEdit.md;
	} 

	// `disabled` của bộ cũ cũng chỉ là "không cho sửa" -> gộp vào readOnly.
	// (Biểu thức động đã đặt readOnly dạng script ở trên nên nhánh này không đụng vào nữa.)
	if ((v.readOnly === true || v.disabled === true) && out.readOnly === undefined) {
		out.readOnly = true;
		rs.applied.push('readOnly');
	}
	// `minWidth` -> bề rộng TỐI THIỂU của cột trên lưới (`tableWidth`). Khai ở cấp field
	// (`min_width_display`) thắng, đúng luật chung của grid_configs.
	if (out.tableWidth === undefined && Number(v.minWidth) > 0) {
		out.tableWidth = Number(v.minWidth);
		rs.applied.push('minWidth');
	}
	for (const k of ['format', 'min', 'max', 'step']) {
		if (v[k] === undefined) continue;
		if (out[k] !== undefined) { rs.skipped.push(k); continue; }   // khai cấp field thắng
		out[k] = v[k];
		rs.applied.push(k);
	}

	rs.ignored = unsupportedGridKeys(v);
	return rs;
}

export {
	GRID_SIZE_KEYS, IGNORED_KEYS, APPLIED_KEYS, DYNAMIC_VARS,
	stripReturn, isDynamicGridConfigs, parseGridConfigs, unsupportedGridKeys,
	gridSize, readOnlyScriptFrom, applyGridConfigs,
};
